"use client";

import { CheckCircle2, LoaderCircle, XCircle } from "lucide-react";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { uiHaptics } from "@/lib/ui-haptics";
import { cn } from "@/lib/utils";

export type PaymentScreenState = "processing" | "success" | "failed" | null;

const PROCESSING_HINT =
  "Do not close this screen. If your bank asks for OTP or app approval, finish that step and return here.";

type PaymentStatusOverlayProps = {
  state: PaymentScreenState;
  message: string;
  /** Copy for the three states; defaults suit any Razorpay charge. */
  processingTitle?: string;
  successTitle?: string;
  failedTitle?: string;
  /** Hidden by passing null — some flows (subscriptions) redirect instead. */
  processingHint?: string | null;
  retryLabel?: string;
  /**
   * Must actually re-attempt the payment.
   *
   * Every caller used to pass `() => setScreenState(null)` — a button labelled
   * "Try again" that only closed the overlay, revealing an inline banner telling
   * the person to retry somewhere else. If all you want is to close this, use
   * `onDismiss`, which says so.
   */
  onRetry?: () => void;
  /** Secondary, quieter action. Rendered as a text button under the primary. */
  dismissLabel?: string;
  onDismiss?: () => void;
};

/**
 * The full-screen processing / confirmed / failed overlay shared by every
 * Razorpay checkout screen. Sits above the payment shell footer (z-1100) so a
 * captured payment can never be re-triggered from underneath it.
 */
export function PaymentStatusOverlay({
  state,
  message,
  processingTitle = "Processing Payment",
  successTitle = "Payment Confirmed",
  failedTitle = "Payment Failed",
  processingHint = PROCESSING_HINT,
  retryLabel = "Try again",
  onRetry,
  dismissLabel = "Back to payment",
  onDismiss,
}: PaymentStatusOverlayProps) {
  // The one moment a payment resolves — every checkout funnels through here.
  useEffect(() => {
    if (state === "success") uiHaptics.success();
    else if (state === "failed") uiHaptics.error();
  }, [state]);

  if (!state) return null;

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/78 px-5 backdrop-blur-md">
      <div className="flex w-full max-w-[340px] flex-col items-center text-center text-white">
        <div
          className={cn(
            "flex h-20 w-20 items-center justify-center rounded-full border",
            state === "success"
              ? "border-emerald-300/40 bg-emerald-400 text-black shadow-2xl shadow-emerald-400/25"
              : state === "failed"
                ? "border-red-300/35 bg-red-400 text-black shadow-2xl shadow-red-400/20"
                : "border-white/[0.14] bg-glass-bg-08 text-white shadow-2xl shadow-black/35",
          )}
        >
          <div className="relative flex h-12 w-12 items-center justify-center">
            {state === "processing" ? (
              <>
                <span className="absolute h-12 w-12 animate-ping rounded-full border border-white/30" />
                <LoaderCircle
                  className="h-9 w-9 animate-spin"
                  aria-hidden="true"
                />
              </>
            ) : state === "success" ? (
              <CheckCircle2 className="h-10 w-10" aria-hidden="true" />
            ) : (
              <XCircle className="h-10 w-10" aria-hidden="true" />
            )}
          </div>
        </div>

        <div className="mt-6 text-2xl leading-tight font-semibold">
          {state === "processing"
            ? processingTitle
            : state === "success"
              ? successTitle
              : failedTitle}
        </div>
        <div className="mt-2 max-w-[280px] text-sm text-white/62">
          {message}
        </div>

        {state === "processing" && processingHint ? (
          <div className="mt-5 rounded-2xl border border-white/[0.1] bg-glass-bg-04 p-3 text-sm text-white/58">
            {processingHint}
          </div>
        ) : null}

        {state === "failed" && (onRetry || onDismiss) ? (
          <div className="mt-6 flex w-full flex-col items-stretch gap-1">
            {onRetry ? (
              <Button
                type="button"
                className="h-12 w-full rounded-2xl bg-white font-semibold text-black hover:bg-white/90"
                onClick={onRetry}
              >
                {retryLabel}
              </Button>
            ) : null}
            {onDismiss ? (
              <Button
                type="button"
                variant="ghost"
                className="h-11 w-full rounded-2xl text-sm font-medium text-white/70 hover:bg-white/[0.06] hover:text-white dark:text-white/70 dark:hover:bg-white/[0.06] dark:hover:text-white"
                onClick={onDismiss}
              >
                {dismissLabel}
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
